import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Search, Send, Message } from '../assets/icons';
import apiConfig from '../config/api_config';
import '../assets/css/pages/messages.css';

const MessagesPage = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [showMobileList, setShowMobileList] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Redirect if not authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: '/messages' } });
        }
    }, [isAuthenticated, navigate]);

    // Mock data functions
    const getMockConversations = useCallback(() => [
        {
            id: 1,
            participants: [
                { id: 1, first_name: 'Dr.', last_name: 'Adeyemi', email: 'adeyemi@aquavet.com', role: 'Veterinarian' },
                { id: user?.id, first_name: user?.first_name, last_name: user?.last_name }
            ],
            last_message: { content: 'Your fish samples have been analyzed. The results look good!', created_at: new Date().toISOString() },
            unread_count: 2
        },
        {
            id: 2,
            participants: [
                { id: 2, first_name: 'AquaFeed', last_name: 'Store', email: 'support@aquafeed.ng', role: 'Vendor' },
                { id: user?.id, first_name: user?.first_name, last_name: user?.last_name }
            ],
            last_message: { content: 'Your order #1234 has been shipped!', created_at: new Date(Date.now() - 86400000).toISOString() },
            unread_count: 0
        },
        {
            id: 3,
            participants: [
                { id: 3, first_name: 'Lagos', last_name: 'Fish Lab', email: 'lab@lagosfish.com', role: 'Laboratory' },
                { id: user?.id, first_name: user?.first_name, last_name: user?.last_name }
            ],
            last_message: { content: 'Lab results are ready for collection', created_at: new Date(Date.now() - 172800000).toISOString() },
            unread_count: 1
        }
    ], [user?.id, user?.first_name, user?.last_name]);

    const getMockMessages = useCallback((conversationId) => {
        const baseMessages = [
            { id: 1, content: 'Hello! How can I help you today?', sender: { id: conversationId }, created_at: new Date(Date.now() - 3600000).toISOString() },
            { id: 2, content: 'I have some questions about my fish health.', sender: user, created_at: new Date(Date.now() - 3000000).toISOString() },
            { id: 3, content: 'Of course! What symptoms are you seeing?', sender: { id: conversationId }, created_at: new Date(Date.now() - 2400000).toISOString() },
            { id: 4, content: 'Some of them seem lethargic and are not eating well.', sender: user, created_at: new Date(Date.now() - 1800000).toISOString() },
            { id: 5, content: 'That could be a water quality issue. Have you tested your water parameters recently?', sender: { id: conversationId }, created_at: new Date(Date.now() - 1200000).toISOString() },
        ];
        return baseMessages;
    }, [user]);

    // Fetch conversations
    const fetchConversations = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiConfig.get('/messages/conversations/');
            const data = response?.data?.data || response?.data || [];
            setConversations(data);
            
            // Auto-select first conversation if none selected
            if (data.length > 0 && !activeConversation) {
                setActiveConversation(data[0]);
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
            // Use mock data for demo
            setConversations(getMockConversations());
        } finally {
            setLoading(false);
        }
    }, [activeConversation, getMockConversations]);

    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    // Fetch messages for active conversation
    const fetchMessages = useCallback(async (conversationId) => {
        try {
            const response = await apiConfig.get(`/messages/conversations/${conversationId}/messages/`);
            const data = response?.data?.data || response?.data || [];
            setMessages(data);
        } catch (error) {
            console.error('Error fetching messages:', error);
            // Use mock data for demo
            setMessages(getMockMessages(conversationId));
        }
    }, [getMockMessages]);

    useEffect(() => {
        if (activeConversation) {
            fetchMessages(activeConversation.id);
        }
    }, [activeConversation, fetchMessages]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeConversation) return;

        setSending(true);
        const messageText = newMessage;
        setNewMessage('');

        // Optimistic update
        const tempMessage = {
            id: Date.now(),
            content: messageText,
            sender: user,
            created_at: new Date().toISOString(),
            is_read: false
        };
        setMessages(prev => [...prev, tempMessage]);

        try {
            await apiConfig.post(`/messages/conversations/${activeConversation.id}/messages/`, {
                content: messageText
            });
        } catch (error) {
            console.error('Error sending message:', error);
            // Message already added optimistically
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const handleConversationClick = (conversation) => {
        setActiveConversation(conversation);
        setShowMobileList(false);
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        }
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getOtherParticipant = (conversation) => {
        if (!conversation?.participants) return { name: 'Unknown', initials: '?' };
        const other = conversation.participants.find(p => p.id !== user?.id);
        if (!other) return { name: 'Unknown', initials: '?' };
        return {
            ...other,
            name: `${other.first_name || ''} ${other.last_name || ''}`.trim() || other.email,
            initials: `${other.first_name?.charAt(0) || ''}${other.last_name?.charAt(0) || ''}`.toUpperCase() || '?'
        };
    };

    const filteredConversations = conversations.filter(conv => {
        const other = getOtherParticipant(conv);
        return other.name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="messages-page">
            <div className="messages-container">
                {/* Conversations List */}
                <div className={`conversations-panel ${!showMobileList ? 'mobile-hidden' : ''}`}>
                    <div className="conversations-header">
                        <h2>Messages</h2>
                        <button className="new-message-btn" title="New Message">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                                <line x1="12" y1="8" x2="12" y2="16"/>
                                <line x1="8" y1="12" x2="16" y2="12"/>
                            </svg>
                        </button>
                    </div>

                    <div className="conversations-search">
                        <img src={Search} alt="" className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="conversations-list">
                        {loading ? (
                            <div className="loading-conversations">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="conversation-skeleton">
                                        <div className="skeleton-avatar"></div>
                                        <div className="skeleton-content">
                                            <div className="skeleton-name"></div>
                                            <div className="skeleton-message"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredConversations.length === 0 ? (
                            <div className="no-conversations">
                                <img src={Message} alt="" className="empty-icon" />
                                <p>No conversations yet</p>
                                <span>Start a conversation with a vet, vendor, or lab</span>
                            </div>
                        ) : (
                            filteredConversations.map(conversation => {
                                const other = getOtherParticipant(conversation);
                                const isActive = activeConversation?.id === conversation.id;
                                
                                return (
                                    <div
                                        key={conversation.id}
                                        className={`conversation-item ${isActive ? 'active' : ''} ${conversation.unread_count > 0 ? 'unread' : ''}`}
                                        onClick={() => handleConversationClick(conversation)}
                                    >
                                        <div className="conversation-avatar">
                                            {other.initials}
                                        </div>
                                        <div className="conversation-content">
                                            <div className="conversation-header">
                                                <span className="conversation-name">{other.name}</span>
                                                <span className="conversation-time">
                                                    {formatTime(conversation.last_message?.created_at)}
                                                </span>
                                            </div>
                                            <div className="conversation-preview">
                                                <span className="conversation-role">{other.role}</span>
                                                <p className="last-message">
                                                    {conversation.last_message?.content || 'No messages yet'}
                                                </p>
                                            </div>
                                            {conversation.unread_count > 0 && (
                                                <span className="unread-badge">{conversation.unread_count}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Messages Panel */}
                <div className={`messages-panel ${showMobileList ? 'mobile-hidden' : ''}`}>
                    {activeConversation ? (
                        <>
                            <div className="messages-header">
                                <button className="back-btn mobile-only" onClick={() => setShowMobileList(true)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15 18 9 12 15 6"></polyline>
                                    </svg>
                                </button>
                                <div className="chat-participant">
                                    <div className="participant-avatar">
                                        {getOtherParticipant(activeConversation).initials}
                                    </div>
                                    <div className="participant-info">
                                        <span className="participant-name">
                                            {getOtherParticipant(activeConversation).name}
                                        </span>
                                        <span className="participant-role">
                                            {getOtherParticipant(activeConversation).role}
                                        </span>
                                    </div>
                                </div>
                                <div className="chat-actions">
                                    <button className="action-btn" title="Call">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                    </button>
                                    <button className="action-btn" title="Video Call">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polygon points="23 7 16 12 23 17 23 7"/>
                                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                                        </svg>
                                    </button>
                                    <button className="action-btn" title="More Options">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="1"/>
                                            <circle cx="19" cy="12" r="1"/>
                                            <circle cx="5" cy="12" r="1"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="messages-body">
                                {messages.map((message, index) => {
                                    const isOwn = message.sender?.id === user?.id;
                                    const showDate = index === 0 || 
                                        new Date(message.created_at).toDateString() !== 
                                        new Date(messages[index - 1]?.created_at).toDateString();
                                    
                                    return (
                                        <React.Fragment key={message.id}>
                                            {showDate && (
                                                <div className="message-date-divider">
                                                    <span>{new Date(message.created_at).toLocaleDateString('en-US', { 
                                                        weekday: 'long', 
                                                        month: 'short', 
                                                        day: 'numeric' 
                                                    })}</span>
                                                </div>
                                            )}
                                            <div className={`message ${isOwn ? 'own' : 'other'}`}>
                                                {!isOwn && (
                                                    <div className="message-avatar">
                                                        {getOtherParticipant(activeConversation).initials}
                                                    </div>
                                                )}
                                                <div className="message-content">
                                                    <p>{message.content}</p>
                                                    <span className="message-time">
                                                        {new Date(message.created_at).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            <form className="message-input-form" onSubmit={handleSendMessage}>
                                <button type="button" className="attach-btn" title="Attach File">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                    </svg>
                                </button>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Type a message..."
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    disabled={sending}
                                />
                                <button 
                                    type="submit" 
                                    className="send-btn"
                                    disabled={!newMessage.trim() || sending}
                                >
                                    <img src={Send} alt="Send" />
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="no-chat-selected">
                            <img src={Message} alt="" className="empty-icon" />
                            <h3>Select a conversation</h3>
                            <p>Choose a conversation from the list to start messaging</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessagesPage;
